/*
 * Copyright (C) 2024 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and
 * should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly
 * prohibited.
 */

export const HAN_TERMINAL_TYPE = 'HAN';
export type TerminalType = 'Single' | 'Dual' | 'Ku+Ka' | 'HAN' | 'HaloNet' | string;

export type ModemType = 'Unknown' | 'M3' | 'IQ800' | 'Starlink/StarShield' | 'Kratos' | 'Axiom Air' | 'Other' | string;

export interface ProvisionedTerminal {
  id: string;
  displayName: string;
  platformType: string;
  terminalType: TerminalType;
  customerGroup: string;
  serviceTier?: string;
  restrictedDefault: boolean;
  tags: string[];
  datapaths: DataPath[];
  contracts: string[];
  belongsToOrgs: string[];
  hanAgentId?: string; // Only present for HAN terminals (ie TerminalType.HAN)
  haloNetSpaceVehicleName?: string; // Only present for HaloNet terminals (ie TerminalType.HaloNet)
}

export interface DataPath {
  id: string;
  label: string;
  antenna: string;
  modems: Modem[];
}

// Valid options from provisioning-service's SatelliteNetwork.java with ViaSat-3 merged to Viasat
export type SatelliteNetwork =
  | 'Arclight-1'
  | 'Viasat'
  | 'LinkWay'
  | 'GX'
  | 'Flex'
  | 'Starlink/StarShield'
  | 'WGS'
  | 'Other'
  | 'RTE';

/**
 * Lists of SatelliteNetworks that require a mac address or sat ip when provisioning a modem.
 * SatelliteNetworks mentioned here should have a corresponding entry in provisioning's SatelliteNetwork.java enum with idRequired set to true
 */
// List of Satellite Networks that require a MAC Address when provisioning a modem
// Only Viasat and LinkWay for now. May need to add more in the future if HAN needs mac addresses for modems on other networks
export const MAC_ADDRESS_NETWORKS: SatelliteNetwork[] = ['Viasat', 'LinkWay'];

// List of Satellite Networks that require a SAT-IP when provisioning a modem
export const SAT_IP_NETWORKS: SatelliteNetwork[] = ['Arclight-1'];

// List of Satellite Networks that require a TPK when provisioning a modem
export const TPK_NETWORKS: SatelliteNetwork[] = ['GX'];

// list of modem types that requires a TPK when provisioning a modem
export const SERIAL_NUMBER_NETWORKS: SatelliteNetwork[] = ['GX'];

// list of Satellite Networks that requires cdp asset ID and serial number when provisioning a modem
export const SERIAL_NUMBER_MODEM_TYPES: ModemType[] = ['Axiom Air'];

export interface Modem {
  id: string;
  type: ModemType;
  satIp: string | null;
  macAddress: string | null;
  tpk: string | null;
  networks: SatelliteNetwork[];
  enclosureLabel: string | null;
  cdpAssetId: string | null;
  serialNumber: string | null;
}

// Matches the Enclosure Group form inputs.
export interface EnclosureGroup {
  label: string; // Enclosure Group Label
  modemFormIdentifiers: string[]; // Modem Form Identifiers (e.g. "Data Path #1: KU | Modem #1")
}

// Structure the TerminalForm uses (combination of Provisioned Terminal and Enclosure Groups)
export interface TerminalFormSchema extends ProvisionedTerminal {
  enclosureGroups: EnclosureGroup[];
}
